import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PermissionAwareButton, PermissionAwareInput, PermissionAwareSelect } from '../../../contexts/PermissionAccessContext';
import type { UnitChildKey, UnitChildLookups } from '../../../types/safetySystem';
import type { ChildRow } from './ticketForm';
import { inputClass, str } from './unitFields';
import { liveRows as live, rowKeyOf, toIsoDate, type UnitSaveErrors } from './unitForm';

/**
 * Editable row grid for the Unit drawer child lists (permits, OW permits, training, ELD,
 * restrictions, insurance, deductions, surveys, awards). Same conventions as the Ticket /
 * Incident row tabs: each cell is a PermissionAware control labelled "<Column> <n>" (n =
 * the row's position among the VISIBLE rows), removing an existing row marks it
 * `deleted:'Y'` (hidden here, posted as a delete marker) and removing a new row drops it.
 * Sits inside the tab's PermissionAccessProvider, so it disables itself below edit level.
 *
 * `errors` (rowKey -> field -> message) renders the server's structured 422 under the cell.
 */

export interface GridOption { value: string; label: string }

export interface GridColumn {
  key: string;
  label: string;
  type?: 'text' | 'date' | 'number' | 'select' | 'readonly';
  options?: GridOption[];
  /** Extra classes on the `<td>` (widths). */
  className?: string;
  /** Extra classes on the input (e.g. the survey rating colour). */
  inputClass?: (row: ChildRow) => string;
  min?: number;
  max?: number;
  step?: number | string;
  maxLength?: number;
  /** Custom patch for a cell change (e.g. deduction type -> default amount). Default sets `row[key] = value`. */
  onChange?: (row: ChildRow, value: string) => ChildRow;
  /** Read-only display of `row[key]` (or `render`). */
  render?: (row: ChildRow) => React.ReactNode;
}

export interface UnitChildGridProps {
  title?: string;
  rows: ChildRow[];
  onRowsChange: (rows: ChildRow[]) => void;
  columns: GridColumn[];
  newRow: () => ChildRow;
  addLabel: string;
  /** Row-removal button label prefix ("Remove permit" -> "Remove permit 1"). */
  removeLabel: string;
  emptyText: string;
  errors?: Record<string, Record<string, string>>;
  /** Highlight / dim a row (e.g. the current ELD device). */
  rowClass?: (row: ChildRow) => string;
  /** Which of the live rows this grid shows (per-instype / per-cardtype / "last 5" views). Default: all. */
  filter?: (row: ChildRow) => boolean;
  /** Rendered between the table and the Add button (e.g. a "Show all" toggle). */
  footer?: React.ReactNode;
  /** Hide the Add button (read-only lists). */
  noAdd?: boolean;
}

const th = 'px-1.5 py-1 text-left text-xs font-semibold uppercase tracking-wide text-muted';
const td = 'px-1 py-0.5 align-top';
const cell = `${inputClass} min-w-[4rem]`;

export function UnitChildGrid({
  title, rows, onRowsChange, columns, newRow, addLabel, removeLabel, emptyText, errors, rowClass, filter, footer, noAdd,
}: UnitChildGridProps) {
  const visible = live(rows).filter((r) => (filter ? filter(r) : true));
  const update = (key: string, patch: (row: ChildRow) => ChildRow) =>
    onRowsChange(rows.map((r) => (rowKeyOf(r) === key ? patch(r) : r)));
  const remove = (key: string) =>
    onRowsChange(rows.flatMap((r) => (rowKeyOf(r) !== key ? [r] : r.recno ? [{ ...r, deleted: 'Y' }] : [])));
  const add = () => onRowsChange([...rows, newRow()]);

  const body = (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className={th}>#</th>
              {columns.map((c) => <th key={c.key} className={th}>{c.label}</th>)}
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={columns.length + 2} className="px-2 py-3 text-center text-sm text-muted">{emptyText}</td></tr>
            )}
            {visible.map((row, i) => {
              const n = i + 1;
              const key = rowKeyOf(row);
              const rowErrors = errors?.[key] ?? {};
              return (
                <tr key={key} className={rowClass?.(row) ?? ''}>
                  <td className={`${td} pt-2 text-xs text-muted`}>{n}</td>
                  {columns.map((c) => {
                    const label = `${c.label} ${n}`;
                    const set = (value: string) => update(key, (r) => (c.onChange ? c.onChange(r, value) : { ...r, [c.key]: value }));
                    const extra = c.inputClass?.(row) ?? '';
                    let control: React.ReactNode;
                    if (c.type === 'readonly') {
                      control = <span aria-label={label} className={`block px-1 py-1 ${extra}`}>{c.render ? c.render(row) : str(row[c.key])}</span>;
                    } else if (c.type === 'select') {
                      const raw = str(row[c.key]);
                      const current = raw && raw !== '0' ? raw : '';
                      const options = c.options ?? [];
                      control = (
                        <PermissionAwareSelect aria-label={label} className={`${cell} ${extra}`} value={current} onChange={(e) => set(e.target.value)}>
                          <option value="">—</option>
                          {current && !options.some((o) => o.value === current) && <option value={current}>{current}</option>}
                          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </PermissionAwareSelect>
                      );
                    } else if (c.type === 'date') {
                      control = <PermissionAwareInput type="date" aria-label={label} className={`${cell} ${extra}`} value={toIsoDate(row[c.key])} onChange={(e) => set(e.target.value)} />;
                    } else {
                      control = (
                        <PermissionAwareInput type={c.type === 'number' ? 'number' : 'text'} aria-label={label} className={`${cell} ${extra}`}
                          value={str(row[c.key])} min={c.min} max={c.max} step={c.step} maxLength={c.maxLength} onChange={(e) => set(e.target.value)} />
                      );
                    }
                    return (
                      <td key={c.key} className={`${td} ${c.className ?? ''}`}>
                        {control}
                        {rowErrors[c.key] && <p role="alert" className="mt-0.5 text-xs text-danger-fg">{rowErrors[c.key]}</p>}
                      </td>
                    );
                  })}
                  <td className={td}>
                    <PermissionAwareButton type="button" aria-label={`${removeLabel} ${n}`} onClick={() => remove(key)}
                      className="rounded p-1 text-muted hover:bg-highlight hover:text-danger-fg disabled:cursor-not-allowed">
                      <TrashIcon width={16} aria-hidden="true" />
                    </PermissionAwareButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer}
      {!noAdd && (
        <PermissionAwareButton type="button" onClick={add}
          className="inline-flex items-center gap-1 rounded-md border border-rule px-2 py-1 text-sm hover:bg-highlight disabled:cursor-not-allowed disabled:text-disabled">
          <PlusIcon width={14} aria-hidden="true" />{addLabel}
        </PermissionAwareButton>
      )}
    </div>
  );

  if (!title) return body;
  return (
    <fieldset className="rounded-md border border-rule p-2">
      <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">{title}</legend>
      {body}
    </fieldset>
  );
}

/** Loading / failed state for a child-row section while GET /children is in flight. */
export const ChildrenStatus: React.FC<{ title: string; loading: boolean; error: string }> = ({ title, loading, error }) => (
  <fieldset className="rounded-md border border-rule p-2">
    <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">{title}</legend>
    {error ? (
      <p role="alert" className="py-2 text-center text-sm text-danger-fg">{error}</p>
    ) : (
      <p className="py-2 text-center text-sm text-muted">{loading ? 'Loading…' : ''}</p>
    )}
  </fieldset>
);

/** The child-list plumbing every Unit drawer tab with grids receives from the drawer. */
export interface ChildListsProps {
  childRows: Partial<Record<UnitChildKey, ChildRow[]>>;
  onChildRowsChange: (key: UnitChildKey, rows: ChildRow[]) => void;
  lookups: UnitChildLookups;
  childrenLoading: boolean;
  childrenError: string;
  rowErrors: UnitSaveErrors['rows'];
}
